// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from '@burdenoff/fe-libs/ui';
import { Save, CalendarDays, Clock, MapPin } from 'lucide-react';
import { usePlanMagnet } from '../providers/PlanMagnetProvider';

const GET_PROJECTS_AND_SPRINTS = gql`
  query GetProjectsAndSprintsForMeetings(
    $workspaceId: ID!
    $projectPagination: PaginationInput
    $sprintPagination: PaginationInput
  ) {
    planmagnetProjects(workspaceId: $workspaceId, pagination: $projectPagination) {
      items {
        id
        name
        key
        status
      }
      totalCount
    }
    planmagnetSprints(workspaceId: $workspaceId, pagination: $sprintPagination) {
      items {
        id
        name
        status
        startDate
        endDate
      }
      totalCount
    }
  }
`;

interface ProjectNode {
  id: string;
  name: string;
  key: string;
  status: string;
}

interface SprintNode {
  id: string;
  name: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
}

interface MeetingsQueryData {
  projects: { items: ProjectNode[]; totalCount: number };
  sprints: { items: SprintNode[]; totalCount: number };
}

export function MeetingsScheduleMeetingPagePage() {
  const { workspaceId } = usePlanMagnet();
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'standup',
    date: '',
    startTime: '',
    endTime: '',
    projectId: '',
    location: '',
  });
  const types = [
    'standup',
    'sprint_review',
    'retrospective',
    'planning',
    'one_on_one',
    'general',
  ] as const;

  const { data, loading, error } = useQuery<MeetingsQueryData>(GET_PROJECTS_AND_SPRINTS, {
    variables: {
      workspaceId,
      projectPagination: { first: 100 },
      sprintPagination: { first: 50 },
    },
    skip: !workspaceId,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-500">Error: {error.message}</div>;
  }

  const projects = data?.planmagnetProjects?.items ?? [];
  const sprints = data?.planmagnetSprints?.items ?? [];
  const activeSprints = sprints.filter((s) => s.status === 'ACTIVE');

  const upcomingMeetings = activeSprints.map((sprint) => ({
    id: sprint.id,
    title: `${sprint.name} Standup`,
    description: `Daily standup for ${sprint.name}`,
    type: 'standup' as const,
    date: sprint.startDate ?? '',
    startTime: '09:00',
    endTime: '09:15',
    location: 'Virtual',
    sprintName: sprint.name,
  }));

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Meeting Scheduler</h1>
          <p className="text-sm text-muted-foreground">
            {activeSprints.length} active sprints &middot; {projects.length} projects
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Schedule New Meeting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1"
                placeholder="e.g. Sprint 14 Review"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Type</label>
                <select
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Project</label>
                <select
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.projectId}
                  onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                >
                  <option value="">None</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.key})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Date</label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Start</label>
                <Input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">End</label>
                <Input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Location</label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="mt-1"
                placeholder="Zoom / Conference Room A"
              />
            </div>
            <Button size="sm" className="w-full">
              <Save className="mr-1 h-4 w-4" />
              Schedule Meeting
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h2 className="text-lg font-medium">Sprint-Based Meetings</h2>
          {upcomingMeetings.map((mtg) => (
            <Card key={mtg.id}>
              <CardContent className="py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{mtg.title}</span>
                  <Badge variant="outline" className="capitalize text-xs">
                    {mtg.type.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{mtg.description}</p>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  {mtg.date && (
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {mtg.date}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {mtg.startTime}-{mtg.endTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {mtg.location}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
          {upcomingMeetings.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  No active sprints found. Start a sprint to generate meeting suggestions.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
